import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { currentLocale } from '../../i18n'
import { getOrganizacionesComercial, getArbolOrganizacionesComercial, getTiposOrganizacionComercial } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { puede, mensajeError, ordenarCatalogo } from '../../components/comercial/comercialLogica'
import {
  filtrosDeParams, paramsConFiltros, hayFiltros, contarFiltrosAdicionales, consultaListado, filtrarEnPagina,
  ordenarOrganizaciones, siguienteOrden, ordenVisible, columnasValidas, alternarColumna, COLUMNAS_OPCIONALES,
  COLUMNAS_PREDETERMINADAS, filtrarArbol, expansionInicial, contarNodos,
} from '../../components/comercial/organizacionesLogica'
import { nombrePais as nombrePaisEn } from '../../components/comercial/paises'
import useFichaComercial from '../../components/comercial/useFichaComercial'
import ComercialFichas from '../../components/comercial/ComercialFichas'
import ComercialVacio from '../../components/comercial/ComercialVacio'
import ComercialOrganizacionesLista from '../../components/comercial/ComercialOrganizacionesLista'
import ComercialOrganizacionesArbol from '../../components/comercial/ComercialOrganizacionesArbol'
import ComercialSelectorPais from '../../components/comercial/ComercialSelectorPais'
import { BotonFiltros, MenuColumnas } from '../../components/comercial/ComercialListadoControles'
import { leerLocal, guardarLocal } from '../../components/comercial/comercialUtiles'

const PASO = 100                                   // rows shown at a time ("Mostrar más")
const CLAVE_VISTA = 'comercial:organizaciones:vista'
const leerVista = () => { try { return localStorage.getItem(CLAVE_VISTA) === 'arbol' ? 'arbol' : 'lista' } catch { return 'lista' } }
const guardarVista = (v) => { try { localStorage.setItem(CLAVE_VISTA, v) } catch { /* private mode */ } }
const CLAVE_COLUMNAS = 'comercial:organizaciones:columnas'
const leerColumnas = () => leerLocal(CLAVE_COLUMNAS, columnasValidas)
const guardarColumnas = (c) => guardarLocal(CLAVE_COLUMNAS, c)
const control = 'border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

// Comercial > CRM > Organizaciones: the list (or the tree) with its search and
// filters, and each organization's card in the side panel (?org=<id>; a new
// one, ?org=nueva). The filters live in the address, so they stay while cards
// open and close; the server filters by text, type, country and state, the
// page by city and top level, sorts and shows PASO rows at a time. The bar
// shows search and type; the rest wait behind "Filtros". The table's columns
// are the user's choice, kept in the browser.
export default function ComercialOrganizaciones() {
  const { t } = useTranslation('comercial')
  const { user } = useAuth()
  const puedeEditar = puede(user, 'comercial_editar')
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const { ficha, abrir } = useFichaComercial()
  const filtros = filtrosDeParams(params)
  const locale = currentLocale()
  const nombrePais = useCallback((c) => nombrePaisEn(c, locale), [locale])

  const [texto, setTexto] = useState(filtros.q)     // the search box; it reaches the address after a pause
  const [ciudad, setCiudad] = useState(filtros.ciudad)     // likewise
  const [vista, setVista] = useState(leerVista)
  const [columnas, setColumnas] = useState(leerColumnas)
  // Open at first if the address already brings some of its filters.
  const [verFiltros, setVerFiltros] = useState(() => contarFiltrosAdicionales(filtros, leerVista()) > 0)
  const [orden, setOrden] = useState({ campo: 'nombre', dir: 'asc' })
  const [mas, setMas] = useState({ clave: '', n: PASO })   // "Mostrar más", for the current filters
  const [lista, setLista] = useState(null)
  const [arbol, setArbol] = useState(null)
  const [tipos, setTipos] = useState([])
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)         // bumped when a card saved something

  // Changing a filter replaces the address (no history entry) and keeps the open card's history depth.
  const estadoRef = useRef(location.state)
  useEffect(() => { estadoRef.current = location.state }, [location.state])
  const fijarFiltros = useCallback((cambio) => {
    setParams(prev => paramsConFiltros(prev, { ...filtrosDeParams(prev), ...cambio }),
      { replace: true, state: estadoRef.current })
  }, [setParams])

  useEffect(() => {
    if (texto.trim() === filtros.q) return undefined
    const pausa = setTimeout(() => fijarFiltros({ q: texto }), 300)
    return () => clearTimeout(pausa)
  }, [texto, filtros.q, fijarFiltros])
  useEffect(() => {
    if (ciudad.trim() === filtros.ciudad) return undefined
    const pausa = setTimeout(() => fijarFiltros({ ciudad }), 300)
    return () => clearTimeout(pausa)
  }, [ciudad, filtros.ciudad, fijarFiltros])

  const consulta = JSON.stringify(consultaListado(filtros))
  useEffect(() => {
    let vivo = true
    getOrganizacionesComercial(JSON.parse(consulta))
      .then(d => { if (vivo) { setLista(d); setError('') } })
      .catch(e => vivo && setError(mensajeError(e, t, t('organizaciones.cargaFallo'))))
    return () => { vivo = false }
  }, [consulta, version, t])
  const claveFilas = `${consulta}|${filtros.nivel}|${filtros.ciudad}`
  const mostrar = mas.clave === claveFilas ? mas.n : PASO     // other filters start again at PASO

  const conArchivadas = filtros.estado !== 'no'
  useEffect(() => {
    if (vista !== 'arbol') return undefined
    let vivo = true
    getArbolOrganizacionesComercial({ archivadas: conArchivadas })
      .then(d => vivo && setArbol(d))
      .catch(e => vivo && setError(mensajeError(e, t, t('organizaciones.cargaFallo'))))
    return () => { vivo = false }
  }, [vista, conArchivadas, version, t])

  useEffect(() => { getTiposOrganizacionComercial().then(d => setTipos(ordenarCatalogo(d))).catch(() => {}) }, [])

  const ordenActual = useMemo(() => ordenVisible(orden, columnas), [orden, columnas])
  const filas = useMemo(
    () => ordenarOrganizaciones(filtrarEnPagina(lista, { nivel: filtros.nivel, ciudad: filtros.ciudad }), ordenActual, nombrePais),
    [lista, filtros.nivel, filtros.ciudad, ordenActual, nombrePais])
  const busquedaArbol = useMemo(() => (arbol ? filtrarArbol(arbol, texto) : null), [arbol, texto])
  const arbolVisible = busquedaArbol?.nodos || arbol
  const abiertosArbol = useMemo(() => (busquedaArbol ? busquedaArbol.abiertos : expansionInicial(arbol || [])),
    [busquedaArbol, arbol])

  const cambiarVista = (v) => { setVista(v); guardarVista(v) }
  const cambiarColumnas = (c) => { setColumnas(c); guardarColumnas(c) }
  const limpiar = () => {
    setTexto('')
    setCiudad('')
    fijarFiltros({ q: '', tipo: null, pais: '', ciudad: '', estado: 'no', nivel: false })
  }
  const conFiltros = hayFiltros({ ...filtros, q: texto, ciudad })
  const adicionales = contarFiltrosAdicionales({ ...filtros, ciudad }, vista)
  const nueva = puedeEditar && (
    <button type="button" onClick={() => abrir('org', 'nueva')}
      className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 inline-flex items-center gap-1.5">
      + {t('organizaciones.nueva')}
    </button>
  )

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-5">
        <PageHeader title={t('organizaciones.titulo')} actions={nueva} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input type="search" value={texto} onChange={e => setTexto(e.target.value)} aria-label={t('organizaciones.buscar')}
          placeholder={t(vista === 'arbol' ? 'organizaciones.buscarArbol' : 'organizaciones.buscar')}
          className={`${control} w-full sm:w-72`} />
        {vista === 'lista' && (
          <select value={filtros.tipo || ''} onChange={e => fijarFiltros({ tipo: Number(e.target.value) || null })}
            aria-label={t('lista.tipos')} className={control}>
            <option value="">{t('organizaciones.todosTipos')}</option>
            {tipos.map(x => <option key={x.id} value={x.id}>{x.nombre}{x.activo ? '' : ` (${t('chips.inactivo').toLowerCase()})`}</option>)}
          </select>
        )}
        <BotonFiltros abierto={verFiltros} cuenta={adicionales} onClick={() => setVerFiltros(v => !v)} controla="filtros-organizaciones" />
        {conFiltros && (
          <button type="button" onClick={limpiar} className="text-sm text-gray-500 underline hover:text-gray-900 px-1">
            {t('organizaciones.limpiar')}
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {vista === 'lista' && (
            <MenuColumnas columnas={columnas} onCambiar={cambiarColumnas} alternar={alternarColumna}
              fija={t('lista.organizacion')} opcionales={COLUMNAS_OPCIONALES} predeterminadas={COLUMNAS_PREDETERMINADAS}
              titulos={{
                tipos: t('lista.tipos'), superior: t('lista.perteneceA'), pais: t('lista.pais'), ciudad: t('lista.ciudad'),
                sitio_web: t('lista.sitioWeb'), identificador: t('lista.identificador'), direccion: t('lista.direccion'),
              }} />
          )}
          <div role="radiogroup" aria-label={t('organizaciones.vista')}
            className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
            {['lista', 'arbol'].map(v => (
              <button key={v} type="button" role="radio" aria-checked={vista === v} onClick={() => cambiarVista(v)}
                className={`px-3 py-1.5 rounded-md ${vista === v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                {t(`organizaciones.vista_${v}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {verFiltros && (
        <div id="filtros-organizaciones" className="flex flex-wrap items-center gap-2 mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
          {vista === 'lista' && (
            <>
              <div className="w-full sm:w-48">
                <ComercialSelectorPais value={filtros.pais} onChange={v => fijarFiltros({ pais: v })}
                  placeholder={t('organizaciones.todosPaises')} textoVacio={t('organizaciones.todosPaises')}
                  aria-label={t('lista.pais')} />
              </div>
              <input type="search" value={ciudad} onChange={e => setCiudad(e.target.value)} aria-label={t('lista.ciudad')}
                placeholder={t('organizaciones.ciudad')} className={`${control} w-full sm:w-44`} />
            </>
          )}
          <select value={filtros.estado} onChange={e => fijarFiltros({ estado: e.target.value })}
            aria-label={t('organizaciones.estado')} className={control}>
            <option value="no">{t('organizaciones.activas')}</option>
            <option value="si">{t('organizaciones.archivadas')}</option>
            <option value="todas">{t('organizaciones.todas')}</option>
          </select>
          {vista === 'lista' && (
            <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer px-1">
              <input type="checkbox" checked={filtros.nivel} onChange={e => fijarFiltros({ nivel: e.target.checked })}
                className="rounded border-gray-300" />
              {t('organizaciones.soloNivelSuperior')}
            </label>
          )}
        </div>
      )}

      {error && (
        <p className="mb-3 text-sm text-red-600">
          {error}{' '}
          <button type="button" onClick={() => setVersion(v => v + 1)} className="underline">{t('organizaciones.reintentar')}</button>
        </p>
      )}

      {vista === 'lista' && (
        !lista ? (!error && <p className="text-sm text-gray-400">{t('panel.cargando')}</p>)
        : !filas.length ? (
          conFiltros
            ? <ComercialVacio titulo={t('organizaciones.sinResultados')} texto={t('organizaciones.sinResultadosTexto')}>
                <button type="button" onClick={limpiar} className="text-sm text-gray-700 underline">{t('organizaciones.limpiar')}</button>
              </ComercialVacio>
            : <ComercialVacio titulo={t('organizaciones.vacio')} texto={t(puedeEditar ? 'organizaciones.vacioTexto' : 'organizaciones.vacioTextoVer')}>
                {nueva}
              </ComercialVacio>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-2">{t('organizaciones.contador', { count: filas.length })}</p>
            <ComercialOrganizacionesLista filas={filas.slice(0, mostrar)} columnas={columnas} orden={ordenActual}
              onOrden={c => setOrden(o => siguienteOrden(ordenVisible(o, columnas), c))}
              actual={ficha?.tipo === 'org' ? ficha.id : null} onAbrir={id => abrir('org', id)} nombrePais={nombrePais} />
            {filas.length > mostrar && (
              <div className="mt-3 text-center">
                <button type="button" onClick={() => setMas({ clave: claveFilas, n: mostrar + PASO })}
                  className="px-4 py-2 rounded-lg text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                  {t('organizaciones.mostrarMas', { count: filas.length - mostrar })}
                </button>
              </div>
            )}
          </>
        )
      )}

      {vista === 'arbol' && (
        !arbol ? (!error && <p className="text-sm text-gray-400">{t('panel.cargando')}</p>)
        : !arbolVisible.length ? (
          texto.trim()
            ? <ComercialVacio titulo={t('organizaciones.sinResultados')} texto={t('organizaciones.sinResultadosArbol')} />
            : <ComercialVacio titulo={t('organizaciones.vacio')} texto={t(puedeEditar ? 'organizaciones.vacioTexto' : 'organizaciones.vacioTextoVer')}>
                {nueva}
              </ComercialVacio>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-2">
              {busquedaArbol
                ? t('organizaciones.coincidencias', { count: busquedaArbol.coincidencias.size })
                : t('organizaciones.contador', { count: contarNodos(arbol) })}
            </p>
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
              <ComercialOrganizacionesArbol key={`${conArchivadas}-${texto.trim()}-${version}`} nodos={arbolVisible}
                abiertosIniciales={abiertosArbol} resaltar={texto} coincidencias={busquedaArbol?.coincidencias}
                actual={ficha?.tipo === 'org' ? ficha.id : null} onAbrir={id => abrir('org', id)} herramientas />
            </div>
          </>
        )
      )}

      <ComercialFichas onCambio={() => setVersion(v => v + 1)} />
    </div>
  )
}
